// What a custom field filter asks of one stored value, and whether the value meets it. Pure: both the
// Opportunities and the Projects list match through these functions, so the two lists cannot disagree.
// Stored values are the custom field service's strings: numbers with '.', dates as YYYY-MM-DD, and a
// dropdown value as the option's numeric key.
import moment, {type Moment} from 'moment';
import {
	compareCustomFieldSortValues,
	customFieldSortValue,
	type CustomFieldColumnDef,
} from '../custom-field-columns/customFieldColumnModel';
import {isDatePreset, resolveDatePreset, type DatePreset} from './customFieldFilterDates';

export type TextCondition = {op: 'contains'; value: string} | {op: 'is'; value: string} | {op: 'empty'};
export type NumberCondition =
	| {op: 'equals'; value: string}
	| {op: 'gt'; value: string}
	| {op: 'lt'; value: string}
	| {op: 'between'; from: string; to: string}
	| {op: 'empty'};
export type DateCondition = {op: 'preset'; preset: DatePreset} | {op: 'between'; from: string; to: string} | {op: 'empty'};
export type ValueCondition = TextCondition | NumberCondition | DateCondition;

// A dropdown filter stores the selected options' global ids; this entry stands for "is empty".
export const EMPTY_OPTION = 'EMPTY';

// Never set, cleared to blank, or only spaces.
export const isBlankValue = (raw: string | null | undefined): boolean => raw == null || raw.trim() === '';

const INTEGER_PATTERN = /^-?\d+$/;
const DECIMAL_PATTERN = /^-?\d+(\.\d+)?$/;
const isIsoDate = (value: unknown): value is string => typeof value === 'string' && moment(value, 'YYYY-MM-DD', true).isValid();
const isStoredNumber = (def: CustomFieldColumnDef, value: unknown): value is string =>
	typeof value === 'string' && (def.kind === 'INTEGER' ? INTEGER_PATTERN : DECIMAL_PATTERN).test(value);
const isNonBlankString = (value: unknown): value is string => typeof value === 'string' && value.trim() !== '';

/** Whether a stored condition fits the field's type. Saved filters are user data and may be stale or malformed. */
export function isValidCondition(def: CustomFieldColumnDef, value: unknown): value is ValueCondition {
	if (!value || typeof value !== 'object') return false;
	const condition = value as Record<string, unknown>;
	switch (def.kind) {
		case 'TEXT':
			return (
				condition.op === 'empty' ||
				((condition.op === 'contains' || condition.op === 'is') && isNonBlankString(condition.value))
			);
		case 'INTEGER':
		case 'DECIMAL':
			if (condition.op === 'empty') return true;
			if (condition.op === 'between') return isStoredNumber(def, condition.from) && isStoredNumber(def, condition.to);
			return (
				(condition.op === 'equals' || condition.op === 'gt' || condition.op === 'lt') &&
				isStoredNumber(def, condition.value)
			);
		case 'DATE':
			if (condition.op === 'empty') return true;
			if (condition.op === 'preset') return isDatePreset(condition.preset);
			return condition.op === 'between' && isIsoDate(condition.from) && isIsoDate(condition.to);
		default:
			return false;
	}
}

const textMatches = (raw: string, condition: TextCondition): boolean => {
	const actual = raw.trim().toLowerCase();
	switch (condition.op) {
		case 'contains':
			return actual.includes(condition.value.trim().toLowerCase());
		case 'is':
			return actual === condition.value.trim().toLowerCase();
		default:
			return false;
	}
};

// Exact decimal-string comparison, shared with the columns' sort, so long values are never rounded.
const numberMatches = (def: CustomFieldColumnDef, raw: string, condition: NumberCondition): boolean => {
	const compare = compareCustomFieldSortValues(def);
	const actual = customFieldSortValue(def, raw);
	if (actual === undefined) return false;
	const against = (value: string): number | undefined => {
		const bound = customFieldSortValue(def, value);
		return bound === undefined ? undefined : compare(actual, bound);
	};
	switch (condition.op) {
		case 'equals':
			return against(condition.value) === 0;
		case 'gt':
			return (against(condition.value) ?? 0) > 0;
		case 'lt':
			return (against(condition.value) ?? 0) < 0;
		case 'between': {
			const low = against(condition.from);
			const high = against(condition.to);
			return low !== undefined && high !== undefined && low >= 0 && high <= 0;
		}
		default:
			return false;
	}
};

// YYYY-MM-DD strings order the same as the dates they name.
const dateMatches = (raw: string, condition: DateCondition, today: Moment): boolean => {
	if (!isIsoDate(raw)) return false;
	const range =
		condition.op === 'preset'
			? resolveDatePreset(condition.preset, today)
			: condition.op === 'between'
			? {from: condition.from, to: condition.to}
			: null;
	return range !== null && raw >= range.from && raw <= range.to;
};

/** One condition against one stored value. Only "is empty" can match an empty value. */
export function conditionMatches(
	def: CustomFieldColumnDef,
	raw: string | null | undefined,
	condition: ValueCondition,
	today: Moment
): boolean {
	if (condition.op === 'empty') return isBlankValue(raw);
	if (isBlankValue(raw)) return false;
	const value = raw as string;
	switch (def.kind) {
		case 'TEXT':
			return textMatches(value, condition as TextCondition);
		case 'INTEGER':
		case 'DECIMAL':
			return numberMatches(def, value, condition as NumberCondition);
		case 'DATE':
			return dateMatches(value, condition as DateCondition, today);
		default:
			return false;
	}
}

/** A dropdown value against the selected option ids, any of which may match. */
export function dropdownMatches(
	def: CustomFieldColumnDef,
	raw: string | null | undefined,
	selected: readonly unknown[]
): boolean {
	if (isBlankValue(raw)) return selected.includes(EMPTY_OPTION);
	return selected.some(id => def.options.find(option => option.id === id)?.key === raw);
}

/** The field's own verdict for its whole selection, before Exclude is applied. */
export function customFieldFilterHit(
	def: CustomFieldColumnDef,
	raw: string | null | undefined,
	selection: readonly unknown[],
	today: Moment
): boolean {
	if (def.kind === 'DROPDOWN') return dropdownMatches(def, raw, selection);
	return selection.some(condition => isValidCondition(def, condition) && conditionMatches(def, raw, condition, today));
}
